import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import moment from 'moment'

import { checkLicensePermission } from '../../../../utility/communityLicense'
import { isOnlineOnly } from '../../../../utility/programHelper'
import { genderLabel } from '../../../../utility/userHelper'
import { formatPhoneForLinks } from '../../../../utility/phoneHelper'
import { deserialize } from '../../../../utility/serialization'
import { addStudentToProgram, requestGetWaitlistEntry } from '../../../../actions/waitlistActions'
import * as Constants from '../../../../misc/Constants'

import ProfileAvatar from '../../../common/avatar/ProfileAvatar'
import Loader from '../../../common/loader/Loader'
import WaitlistActionModal from '../enrollments-tab/waitlist/modals/WaitlistActionModal'
import CreateEnrollmentModal from '../add-students-modal/CreateEnrollmentModal'
import RemoveStudentForm from '../enrollments-tab/waitlist/forms/RemoveStudentForm'

import { ReactComponent as CloseIcon } from '../../../../assets/images/common/icons/modal-X-icon.svg'
import { ReactComponent as PlusIcon } from '../../../../assets/images/common/icons/circle-plus-icon.svg'
import { ReactComponent as DeleteIcon } from '../../../../assets/images/common/icons/delete-icon.svg'
import { ReactComponent as PersonIcon } from '../../../../assets/images/common/icons/person-icon.svg'
import { ReactComponent as FamilyIcon } from '../../../../assets/images/common/icons/family-icon.svg'
import { ReactComponent as CallIcon } from '../../../../assets/images/common/icons/call-icon.svg'
import { ReactComponent as MailIcon } from '../../../../assets/images/common/icons/mail-icon.svg'
import { ReactComponent as CopyIcon } from '../../../../assets/images/common/icons/copy-icon.svg'
import { ReactComponent as CalendarIcon } from '../../../../assets/images/common/icons/calendar-icon.svg'
import { ReactComponent as GolferIcon } from '../../../../assets/images/common/icons/golfer-half-icon.svg'

import classes from './WaitlistDetailDrawer.module.scss'

const TABS = { enrollments: 'enrollments', contact: 'contact' }
const ACTIONS = { add: 'ADD', remove: 'REMOVE' }

const WaitlistDetailDrawer = ({ waitlistEntry, program, onClose }) => {
  const { t } = useTranslation()
  const community = useSelector(state => state.user.current_user.communities[0])
  const [activeTab, setActiveTab] = useState(TABS.enrollments)
  const dispatch = useDispatch()
  const [action, setAction] = useState(null)
  const [isAddingToProgram, setIsAddingToProgram] = useState(false)
  const [details, setDetails] = useState(null)
  const [isLoadingDetails, setIsLoadingDetails] = useState(true)

  const student = waitlistEntry.user
  const coach = program?.coaches?.[0]
  const coachName = coach ? `${coach.firstName} ${coach.lastName}` : '-'
  const programDates = program?.startDate && program?.endDate
    ? `${moment(program.startDate).format('MM/DD/YYYY')}-${moment(program.endDate).format('MM/DD/YYYY')}`
    : '-'

  const canAddToProgram = !checkLicensePermission(community?.licenseType, [Constants.LICENSES.REVENUE_SHARE])
    && !isOnlineOnly(program?.paymentMethod)

  useEffect(() => {
    let active = true
    setIsLoadingDetails(true)
    requestGetWaitlistEntry(program?.id, waitlistEntry.id)
      .then(response => { if (active) { setDetails(deserialize(response.data)) } })
      .catch(() => toast.error('Failed to load contact details', { position: toast.POSITION.TOP_RIGHT }))
      .finally(() => { if (active) { setIsLoadingDetails(false) } })
    return () => { active = false }
  }, [program?.id, waitlistEntry.id])

  const closeAction = () => setAction(null)
  const closeAfterAction = () => { setAction(null); onClose() }

  // The same billing step as adding a student straight to the program
  const handleAddToProgram = (billing) => {
    setIsAddingToProgram(true)
    dispatch(addStudentToProgram({ course: program, waitlistEntry, ...billing })).unwrap()
      .then(closeAfterAction)
      .catch(() => toast.error(t('programs.manage.addStudentForm.errorAddingStudent'), { position: toast.POSITION.TOP_RIGHT }))
      .finally(() => setIsAddingToProgram(false))
  }

  return (
    <>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div className={classes.overlay} onClick={onClose} />
      <aside className={classes.drawer}>
        <div className={classes.header}>
          <button className={classes.closeButton} onClick={onClose} aria-label='Close'>
            <CloseIcon className={classes.closeIcon} />
          </button>
        </div>

        <div className={classes.tabs}>
          <button
            className={classNames(classes.tab, { [classes.tabActive]: activeTab === TABS.enrollments })}
            onClick={() => setActiveTab(TABS.enrollments)}
          >
            {t('programs.manage.waitlistTab.drawer.tabs.enrollments')}
          </button>
          <button
            className={classNames(classes.tab, { [classes.tabActive]: activeTab === TABS.contact })}
            onClick={() => setActiveTab(TABS.contact)}
          >
            {t('programs.manage.waitlistTab.drawer.tabs.contactInfo')}
          </button>
        </div>

        <div className={classes.body}>
          <div className={classes.studentCard}>
            <ProfileAvatar student={student} />
            <div className='d-flex flex-column'>
              <span className={classes.studentName}>{student.fullName}</span>
              <span className={classes.studentEmail}>{student.email}</span>
            </div>
          </div>

          {activeTab === TABS.enrollments ? (
            <>
              <div className={classes.actions}>
                {canAddToProgram && (
                  <button className={classes.actionButton} onClick={() => setAction(ACTIONS.add)}>
                    <PlusIcon className={classes.actionIcon} />
                    {t('programs.manage.waitlistActions.addToProgram')}
                  </button>
                )}
                <button className={classes.actionButton} onClick={() => setAction(ACTIONS.remove)}>
                  <DeleteIcon className={classes.actionIcon} />
                  {t('common.action.remove')}
                </button>
              </div>

              <div className={classes.detailsCard}>
                <div className={classes.detailsHeader}>
                  <span className={classes.detailsTitle}>{t('programs.manage.waitlistTab.drawer.details')}</span>
                  <span className={classes.waitlistBadge}>{t('programs.manage.waitlistTab.drawer.waitlist')}</span>
                </div>

                <div className={classes.detailRow}>
                  <span className={classes.detailLabel}>{t('programs.manage.waitlistTab.drawer.program')}</span>
                  <span className={classes.detailValue}>{program?.name || '-'}</span>
                </div>
                <div className={classes.detailRow}>
                  <span className={classes.detailLabel}>{t('programs.manage.waitlistTab.drawer.facility')}</span>
                  <span className={classes.detailValue}>{community?.name || '-'}</span>
                </div>
                <div className={classes.detailRow}>
                  <span className={classes.detailLabel}>{t('programs.manage.waitlistTab.drawer.coach')}</span>
                  <span className={classes.detailValue}>{coachName}</span>
                </div>
                <div className={classes.detailRow}>
                  <span className={classes.detailLabel}>{t('programs.manage.waitlistTab.drawer.familyAdminEmail')}</span>
                  <span className={classes.detailValue}>{student.familyAdminsEmails?.[0] || '-'}</span>
                </div>
                <div className={classes.detailRow}>
                  <span className={classes.detailLabel}>{t('programs.manage.waitlistTab.drawer.programDates')}</span>
                  <span className={classes.detailValue}>{programDates}</span>
                </div>

                <p className={classes.updatesTitle}>{t('programs.manage.waitlistTab.drawer.allUpdates')}</p>
                <div className={classes.updateRow}>
                  <span className={classes.updateDot} />
                  <span className={classes.updateDate}>
                    {waitlistEntry.createdAt ? moment(waitlistEntry.createdAt).format('MM/DD/YYYY') : '-'}
                  </span>
                  <span className={classes.updateText}>
                    {t('programs.manage.waitlistTab.drawer.studentJoined', {
                      time: waitlistEntry.createdAt ? moment(waitlistEntry.createdAt).format('h:mm A') : '',
                    })}
                  </span>
                </div>
              </div>
            </>
          ) : (
            <ContactTab details={details} isLoading={isLoadingDetails} />
          )}
        </div>
      </aside>

      {action === ACTIONS.add && (
        <CreateEnrollmentModal
          isOpen
          onClose={() => setAction(null)}
          program={program}
          students={[waitlistEntry.user]}
          onSubmit={handleAddToProgram}
          isLoading={isAddingToProgram}
        />
      )}

      {action === ACTIONS.remove && (
        <WaitlistActionModal
          isOpen
          onClose={closeAction}
          student={student}
          title='Remove from Waitlist'
          subtitle='Remove this student from the program, they will receive an email.'
          form={<RemoveStudentForm course={program} waitlistEntry={waitlistEntry} closeModal={closeAfterAction} />}
        />
      )}
    </>
  )
}

WaitlistDetailDrawer.propTypes = {
  waitlistEntry: PropTypes.object.isRequired,
  program: PropTypes.object,
  onClose: PropTypes.func.isRequired,
}

const ContactTab = ({ details, isLoading }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  if (isLoading || !details) {
    return <div className={classes.loaderWrap}><Loader /></div>
  }

  const student = details.student
  const familyAdmin = student.familyAdmins?.[0]
  const programs = details.communityEnrollments || []
  const hasGender = familyAdmin?.gender !== null && familyAdmin?.gender !== undefined

  const handleCopy = (e, text) => {
    e.preventDefault()
    e.stopPropagation()
    navigator.clipboard.writeText(text)
    toast.success('Copied email to clipboard!', { position: toast.POSITION.TOP_RIGHT })
  }

  return (
    <div className={classes.tabContent}>
      <section className={classes.quickActions}>
        <h6 className={classes.sectionTitle}>{t('members.detailsDrawer.quickActions')}</h6>
        <button className={classes.quickButton} onClick={() => navigate(`/profile/${student.id}`)}>
          <PersonIcon width={22} height={22} />
          {t('common.action.viewProfile')}
        </button>
        {student.familyId && (
          <button className={classes.quickButton} onClick={() => navigate(`/members/families/${student.familyId}`)}>
            <FamilyIcon width={22} height={22} />
            {t('members.detailsDrawer.viewFamilyPage')}
          </button>
        )}
      </section>

      {familyAdmin && (
        <section className={classes.contactSection}>
          <h6 className={classes.sectionTitle}>{t('members.contactFamilyAdmin')}</h6>
          <div className={classes.adminProfile}>
            <ProfileAvatar student={familyAdmin} avatarClassName='small-round-image-wrapper' />
            <div className={classes.adminInfo}>
              <div className={classes.adminName}>
                <span className='font-weight-bold'>{familyAdmin.firstName} {familyAdmin.lastName}</span>
                <span className={classes.familyAdminLabel}>{t('members.familyAdmin')}</span>
              </div>
              <span className={classes.adminMeta}>
                {familyAdmin.birthdate && `Age ${familyAdmin.age}`}
                {familyAdmin.birthdate && hasGender && ' / '}
                {hasGender && genderLabel[familyAdmin.gender]?.charAt(0)}
              </span>
            </div>
          </div>

          {familyAdmin.phonePrefix && familyAdmin.phone && (
            <a className={classes.contactRow} href={`tel:${formatPhoneForLinks(familyAdmin.phonePrefix, familyAdmin.phone)}`}>
              <CallIcon className={classes.contactIcon} />
              <span>{familyAdmin.phonePrefix} {familyAdmin.phone}</span>
            </a>
          )}

          {familyAdmin.email && (
            <a className={classes.contactRow} href={`mailto:${familyAdmin.email}`}>
              <MailIcon className={classes.contactIcon} />
              <div className={classes.emailWrapper}>
                <button onClick={e => handleCopy(e, familyAdmin.email)} className={classes.copyButton} aria-label='Copy email address'>
                  <CopyIcon className={classes.copyIcon} />
                </button>
                {familyAdmin.email}
              </div>
            </a>
          )}
        </section>
      )}

      {programs.length > 0 && (
        <section className={classes.programsSection}>
          <h6 className={classes.sectionTitle}>{t('programs.manage.waitlistTab.drawer.programsEnrolled')}</h6>
          {programs.map(enrollment => (
            <div key={enrollment.id} className={classes.programCard}>
              <span className={classes.programName}>{enrollment.courseName}</span>
              {enrollment.courseStartDate && (
                <span className={classes.programMetaRow}>
                  <CalendarIcon className={classes.programCalendarIcon} />
                  <span>
                    {moment(enrollment.courseStartDate).format('MMM D, YYYY')}
                    {enrollment.courseEndDate && ` - ${moment(enrollment.courseEndDate).format('MMM D, YYYY')}`}
                  </span>
                </span>
              )}
              {(enrollment.golfersCount > 0 || enrollment.courseMeetingTimes) && (
                <span className={classes.programMetaRow}>
                  {enrollment.golfersCount > 0 && (
                    <>
                      <GolferIcon className={classes.programGolferIcon} />
                      <span className={classes.programGolferCount}>{enrollment.golfersCount}</span>
                    </>
                  )}
                  {enrollment.courseMeetingTimes && <span>{enrollment.courseMeetingTimes}</span>}
                </span>
              )}
            </div>
          ))}
        </section>
      )}
    </div>
  )
}

ContactTab.propTypes = {
  details: PropTypes.object,
  isLoading: PropTypes.bool,
}

export default WaitlistDetailDrawer
